import { z } from "zod";

const tipoEntregaPermitidos = ["correios", "balcao", "Balcao", "motoboy_letscom"];

// Esquema para Dados do Cliente
const clienteSchema = z.object({
  nome_identificacao: z
    .string()
    .min(1, { message: "O nome de identificação é obrigatório" })
    .min(3, {
      message: "O nome de identificação deve ter pelo menos 3 caracteres",
    }),
  nome_contato: z
    .string()
    .min(1, { message: "O nome da pessoa para contato é obrigatório" })
    .min(3, { message: "O nome de contato deve ter pelo menos 3 caracteres" }),
  documento: z
    .string()
    .min(1, { message: "O CNPJ/CPF é obrigatório" })
    .regex(/^(\d{3}\.\d{3}\.\d{3}-\d{2}|\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2})$/, {
      message: "CNPJ/CPF inválido, formato esperado: 000.000.000-00 ou 00.000.000/0000-00",
    }),
  email: z
    .string()
    .min(1, { message: "O e-mail é obrigatório" })
    .email({ message: "E-mail inválido" }),
  telefone_contato: z
    .string()
    .min(1, { message: "O telefone da pessoa para contato é obrigatório" })
    .regex(/^\(\d{2}\) \d{4,5}-\d{4}$/, {
      message: "Telefone inválido, formato esperado: (XX) XXXXX-XXXX",
    }),
  tipoEntrega: z.string().refine((val) => tipoEntregaPermitidos.includes(val), {
    message: "Selecione um tipo de entrega válido",
  }),
});

// Esquema para Endereço
const enderecoSchema = z.object({
  cep: z
    .string()
    .min(1, { message: "O CEP é obrigatório" })
    .regex(/^\d{5}-\d{3}$/, {
      message: "CEP inválido, formato esperado: XXXXX-XXX",
    }),
  estado: z
    .string()
    .min(1, { message: "O estado é obrigatório" })
    .max(2, { message: "Informe a sigla do estado, por exemplo, SP" }),
  cidade: z.string().min(1, { message: "A cidade é obrigatória" }),
  bairro: z.string().min(1, { message: "O bairro é obrigatório" }),
  numero: z
    .string()
    .min(1, { message: "O número é obrigatório" })
    .regex(/^\d+$/, { message: "O número deve ser um valor numérico" }),
  complemento: z.string().optional(),
  logradouro: z.string().min(1, { message: "O logradouro é obrigatório" }),
});

// Esquema para Usuario do Cliente
const usuarioCliente = z.object({
  nome_identificacao: z
    .string()
    .min(1, { message: "O nome de identificação do usuário é obrigatório" })
    .min(3, {
      message: "O nome de identificação deve ter pelo menos 3 caracteres",
    }),
  email: z
    .string()
    .min(1, { message: "O e-mail do usuário é obrigatório" })
    .email({ message: "E-mail inválido" }),
  documento: z
    .string()
    .trim()
    .optional()
    .refine(
      (value) => {
        if (!value) return true; // não informou → ok
        return value.length >= 11 && value.length <= 18;
      },
      {
        message: "Documento inválido",
      }
    ),
  telefone: z
    .string()
    .min(10, "O telefone do usuário para contato é obrigatório")
    .refine((val) => /^\(\d{2}\)\s?\d{4,5}-\d{4}$/.test(val), {
      message: "Telefone inválido",
    }),
  senha: z.string().min(6, { message: "A senha é obrigatório!" }),
});

// Esquema responsavel pela endreço
const deliveryAddress = z.object({
  name_address_delivery: z
    .string()
    .min(1, { message: "O nome do responsável é obrigatório" })
    .min(3, {
      message: "O nome do responsável deve ter pelo menos 3 caracteres",
    }),
  email_address_delivery: z
    .string()
    .min(1, { message: "O e-mail do responsável é obrigatório" })
    .email({ message: "E-mail inválido" }),
  phone_address_delivery: z
    .string()
    .min(1, { message: "O telefone do responsável para contato é obrigatório" })
    .regex(/^\(\d{2}\) \d{4,5}-\d{4}$/, {
      message: "Telefone inválido, formato esperado: (XX) XXXXX-XXXX",
    }),
  setor_address_delivery: z.string().min(1, { message: "O setor é obrigatório" }),
});

const enderecoEntrega = z.object({
  cep: z
    .string({ required_error: "CEP é obrigatório" })
    .min(9, "CEP deve conter 9 caracteres no formato 00000-000")
    .regex(/^\d{5}-\d{3}$/, "Formato de CEP inválido"),
  estado: z
    .string({ required_error: "Estado é obrigatório" })
    .min(1, { message: "O estado é obrigatório" }),
  cidade: z
    .string({ required_error: "Cidade é obrigatória" })
    .min(1, { message: "A cidade é obrigatória" }),
  bairro: z
    .string({ required_error: "Bairro é obrigatório" })
    .min(1, { message: "O bairro é obrigatório" }),
  numero: z
    .string({ required_error: "Número é obrigatório" })
    .min(1, { message: "O estado é obrigatório" }),
  complemento: z.string().optional(),
  logradouro: z.string().optional(),
});

export function gerarCNPJ() {
  let cnpj = "";
  for (let i = 0; i < 14; i++) {
    cnpj += Math.floor(Math.random() * 10); // número de 0 a 9
  }
  return cnpj;
}

const getFormSchema = () => {
  return z.object({
    cliente: clienteSchema,
    endereco: enderecoSchema,
    usuarioCliente: usuarioCliente,
    deliveryAddress: deliveryAddress,
    // enderecoEntrega: enderecoEntrega.optional(),
  });
};

const initialValues = {
  cliente: {
    documento: "",
    nome_identificacao: "",
    email: "",
    telefone_contato: "",
  },
  usuarioCliente: {
    documento: "",
    nome_identificacao: "",
    email: "",
    telefone: "",
    senha: "",
  },
  endereco: {
    cep: "",
    cidade: "",
    estado: "",
    bairro: "",
    logradouro: "",
    numero: "",
    complemento: "",
  },
  deliveryAddress: {
    name_address_delivery: "",
    email_address_delivery: "",
    setor_address_delivery: "",
    phone_address_delivery: "",
  },
};

// Exportando os esquemas para validação
export {
  clienteSchema,
  deliveryAddress,
  enderecoEntrega,
  enderecoSchema,
  getFormSchema,
  initialValues,
  usuarioCliente,
};
